import type { AutosaveState } from "@hq/client";
import { useAutosaveState } from "@hq/client-react";
import { cn } from "@z/ui";
import { useEffect, useState } from "react";

export interface SaveStatusProps {
  noteId: string;
  className?: string;
}

/**
 * `Saved locally` means the local store commit resolved (research.md Section 2); it is never
 * shown while content exists only in editor memory. `idle` is an untouched note, which by
 * definition lives in the store already. The sync layer owns the separate `Synced` state.
 */
export const SAVE_STATUS_LABELS: Record<AutosaveState, string> = {
  idle: "Saved locally",
  dirty: "Saving…",
  saving: "Saving…",
  saved: "Saved locally",
  error: "Save failed",
};

export function SaveStatus(props: SaveStatusProps) {
  return <NoteSaveStatus key={props.noteId} {...props} />;
}

function NoteSaveStatus({ noteId, className }: SaveStatusProps) {
  const state = useAutosaveState(noteId);
  const saved = state === "idle" || state === "saved";
  const [settled, setSettled] = useState(saved);
  if (!saved && settled) setSettled(false);

  useEffect(() => {
    if (!saved) return;
    // Keep one label through short pauses between keystrokes and quick local commits.
    const timer = setTimeout(() => setSettled(true), 1000);
    return () => clearTimeout(timer);
  }, [saved]);

  const label = state === "error" || (saved && settled) ? SAVE_STATUS_LABELS[state] : "Saving…";
  return (
    <span
      data-save-status={state}
      className={cn(
        "text-xs text-fg-faint select-none",
        state === "error" && "text-danger",
        className,
      )}
    >
      {label}
    </span>
  );
}
